import { fetchPaginatedData } from './api';
import { apiRequest } from '../services/apiInterceptor';
import API_ENDPOINTS, { extractAndRegisterLinks, getResourceLink } from './linkRegistry';

/**
 * Fetches all available exam types
 * @returns {Promise<Array>} - List of exam types
 */
export const getExamTypes = async () => {
  // console.log('[examApi] 📡 getExamTypes called');
  // console.log('[examApi] 📍 Call stack:', new Error().stack.split('\n').slice(0, 8).join('\n'));
  try {
    const apiUrl = API_ENDPOINTS.EXAM_TYPES;
    // console.log('[examApi] 🌐 Making API request to:', apiUrl);
    const response = await apiRequest(apiUrl, { caller: 'examApi.getExamTypes' });
    // console.log('[examApi] ✅ API response received:', response?.length || 0, 'exam types');
    
    // Register any HATEOAS links in the response
    if (Array.isArray(response)) {
      response.forEach(examType => {
        if (examType && examType.id) {
          extractAndRegisterLinks(examType, 'examType');
        }
      });
    }
    
    // Handle different response formats
    if (Array.isArray(response)) {
      return response;
    } else if (response && typeof response === 'object') {
      // Check for common wrapper patterns
      for (const key of ['items', 'data', 'examTypes', 'results', 'payload']) {
        if (Array.isArray(response[key])) {
          response[key].forEach(examType => {
            if (examType && examType.id) {
              extractAndRegisterLinks(examType, 'examType');
            }
          });
          return response[key];
        }
      }
      
      // Look for any array in the response
      for (const key in response) {
        if (Array.isArray(response[key])) {
          response[key].forEach(item => {
            if (item && item.id) {
              extractAndRegisterLinks(item, key.toLowerCase());
            }
          });
          return response[key];
        }
      }
      
      // If the response itself has _links, extract the links
      if (response._links) {
        extractAndRegisterLinks(response, 'examTypeCollection');
      }
    }
    
    // Return empty array if no valid data found
    console.warn('Could not extract exam types from response:', response);
    return [];
  } catch (error) {
    console.error('Error fetching exam types:', error);
    return [];
  }
};

/**
 * Fetches a specific exam type by ID
 * @param {number} id - The ID of the exam type
 * @returns {Promise<Object>} - The exam type details
 */
export const getExamTypeById = async (id) => {
  try {
    // Get the URL using resource link or fall back to the hardcoded endpoint
    const url = getResourceLink('examType', id, 'self') || API_ENDPOINTS.EXAM_TYPE_BY_ID(id);
    const response = await apiRequest(url);
    
    // Register any HATEOAS links
    if (response && response.id) {
      extractAndRegisterLinks(response, 'examType');
    }
    
    return response;
  } catch (error) {
    console.error(`Error fetching exam type ${id}:`, error);
    throw error;
  }
};

/**
 * Fetches category groups for a specific exam type
 * @param {number} examTypeId - The exam type ID to filter by
 * @returns {Promise<Array>} - List of category groups
 */
export const getCategoryGroupsByExamType = async (examTypeId) => {
  try {
    // Try to find a HATEOAS link from the exam type to its category groups
    const categoryGroupsLink = getResourceLink('examType', examTypeId, 'categoryGroups');
    
    // If we have a HATEOAS link, use it; otherwise fall back to hardcoded endpoint
    const url = categoryGroupsLink || API_ENDPOINTS.CATEGORY_GROUPS_BY_EXAM_TYPE(examTypeId);
    const response = await apiRequest(url);
    
    // Register the category group links
    if (Array.isArray(response)) {
      response.forEach(group => {
        if (group && group.id) {
          extractAndRegisterLinks(group, 'categoryGroup');
        }
      });
    }
    
    return response;
  } catch (error) {
    console.error(`Error fetching category groups for exam type ${examTypeId}:`, error);
    return [];
  }
};

/**
 * Extracts parent ID from HATEOAS parent link
 * @param {Object} category - Category object with HATEOAS links
 * @returns {number|null} - Parent ID or null if no parent
 */
const extractParentIdFromHateoas = (category) => {
  if (!category || !category._links || !category._links.parent) {
    return null;
  }
  
  const parentHref = category._links.parent.href;
  if (!parentHref) {
    return null;
  }
  
  // Extract parent ID from href like "/api/category/103"
  const match = parentHref.match(/\/api\/category\/(\d+)$/);
  return match ? parseInt(match[1], 10) : null;
};

/**
 * Builds hierarchical structure from flat category data
 * @param {Array} categories - Flat array of categories
 * @returns {Array} - Hierarchical array with parent/child relationships
 */
const buildCategoryHierarchy = (categories) => {
  if (!Array.isArray(categories)) {
    return [];
  }
  
  // console.log('[buildCategoryHierarchy] Processing categories for hierarchy:', categories.length);
  
  // Create a map for quick lookups and add children arrays
  const categoryMap = {};
  const hierarchicalCategories = [];
  
  // First pass: create map and initialize children arrays
  categories.forEach(category => {
    const categoryWithChildren = {
      ...category,
      children: []
    };
    categoryMap[category.id] = categoryWithChildren;
  });
  
  // Second pass: build hierarchy by linking children to parents
  categories.forEach(category => {
    const parentId = extractParentIdFromHateoas(category);
    const categoryWithChildren = categoryMap[category.id];
    
    if (parentId && categoryMap[parentId]) {
      // This category has a parent - add it to parent's children
      categoryMap[parentId].children.push(categoryWithChildren);
      // console.log(`[buildCategoryHierarchy] Linked ${category.name} as child of parent ID ${parentId}`);
    } else {
      // This is a root category - add it to the main array
      hierarchicalCategories.push(categoryWithChildren);
      // console.log(`[buildCategoryHierarchy] Added ${category.name} as root category`);
    }
  });
  
  // console.log(`[buildCategoryHierarchy] Built hierarchy: ${hierarchicalCategories.length} root categories`);
  hierarchicalCategories.forEach(root => {
    // console.log(`[buildCategoryHierarchy] Root: ${root.name} (${root.children.length} children)`);
  });
  
  return hierarchicalCategories;
};

/**
 * Fetches categories by category group ID and builds hierarchical structure
 * @param {number} categoryGroupId - The category group ID to filter by
 * @param {boolean} [enabled=true] - Whether to fetch only enabled categories
 * @returns {Promise<Array>} - Hierarchical array of categories with parent/child relationships
 */
export const getCategoriesByGroupId = async (categoryGroupId, enabled = true) => {
  try {
    // Try to get HATEOAS link from registered links
    const categoriesLink = getResourceLink('categoryGroup', categoryGroupId, 'categories');
      // If we have a HATEOAS link, use it; otherwise fall back to hardcoded endpoint
    const url = categoriesLink || API_ENDPOINTS.CATEGORIES_BY_GROUP(categoryGroupId, enabled);
    const response = await apiRequest(url);
    
    // Register any category links and process flat response
    if (Array.isArray(response)) {
      response.forEach(category => {
        if (category && category.id) {
          extractAndRegisterLinks(category, 'category');
        }
      });
      
      // Build hierarchical structure from flat data
      const hierarchicalCategories = buildCategoryHierarchy(response);
      // console.log(`[getCategoriesByGroupId] Converted ${response.length} flat categories to ${hierarchicalCategories.length} hierarchical categories`);
      return hierarchicalCategories;
    }
    
    return response;
  } catch (error) {
    console.error(`Error fetching categories for group ${categoryGroupId}:`, error);
    return [];
  }
};

/**
 * Starts a new exam session and automatically fetches related questions and responses
 * @param {Object} sessionData - The session data
 * @param {number} sessionData.examTypeId - The ID of the exam type
 * @param {string} sessionData.type - The type of the session (e.g., 'Practice')
 * @param {string} [sessionData.Title] - Optional Title for the session
 * @returns {Promise<Object>} - The created session data with questions and responses
 */
export const startExamSession = async (sessionData) => {
  try {
    const response = await apiRequest(API_ENDPOINTS.EXAM_START, {
      method: 'POST',
      body: JSON.stringify(sessionData)
    });
    
    // Extract and register all HATEOAS links from the response
    if (response && response.id) {
      extractAndRegisterLinks(response, 'exam');
      
      // Get questions and responses using HATEOAS links if available
      try {
        // console.log('Fetching questions and responses for the new exam session...');
        
        // Extract questions link from the response or use the registered link
        let questionsLink = null;
        if (response._links && response._links.questions && response._links.questions.href) {
          questionsLink = response._links.questions.href;
          // console.log('Found questions link in _links:', questionsLink);
        } else {
          // Try to get from link registry
          questionsLink = getResourceLink('exam', response.id, 'questions');
          // console.log('Found questions link in registry:', questionsLink);
        }
        
        // If still no link, construct one as fallback
        if (!questionsLink && response.id) {
          questionsLink = `/api/exam/${response.id}/questions`;
          // console.log('Constructed questions link as fallback:', questionsLink);
        }
        
        // Extract responses link from the response or use the registered link
        let responsesLink = null;
        if (response._links && response._links.responses && response._links.responses.href) {
          responsesLink = response._links.responses.href;
          // console.log('Found responses link in _links:', responsesLink);
        } else {
          // Try to get from link registry
          responsesLink = getResourceLink('exam', response.id, 'responses');
          // console.log('Found responses link in registry:', responsesLink);
        }
        
        // If still no link, construct one as fallback
        if (!responsesLink && response.id) {
          responsesLink = `/api/exam/${response.id}/responses`;
          // console.log('Constructed responses link as fallback:', responsesLink);
        }
        
        // Fetch questions and responses concurrently
        const [questions, responses] = await Promise.all([
          questionsLink ? getSessionQuestions(response.id) : [],
          responsesLink ? getSessionResponses(response.id) : []
        ]);
        
        // Add questions and responses to the response object
        response.questions = questions;
        response.responses = responses;
        
        // console.log(`Fetched ${questions.length} questions and ${responses.length} responses for exam session ${response.id}`);
      } catch (fetchError) {
        console.error('Error fetching questions and responses:', fetchError);
        // Don't fail the entire operation if fetching questions/responses fails
        response.questions = [];
        response.responses = [];
      }
    }
    
    return response;
  } catch (error) {
    console.error('Error starting exam session:', error);
    throw error;
  }
};

/**
 * Ends an exam session
 * @param {number} sessionId - The ID of the session to end
 * @returns {Promise<Object>} - The updated session data
 */
export const endExamSession = async (sessionId) => {
  try {
    // Try to get the end link from registered links
    const endLink = getResourceLink('exam', sessionId, 'end');
      // If we have a link, use it; otherwise fall back to hardcoded endpoint
    const url = endLink || API_ENDPOINTS.EXAM_END;
    const response = await apiRequest(url, {
      method: 'POST',
      body: JSON.stringify({ id: sessionId })
    });
    
    // Extract and register links from response
    if (response && response.id) {
      extractAndRegisterLinks(response, 'exam');
    }
    
    return response;
  } catch (error) {
    console.error(`Error ending exam session ${sessionId}:`, error);
    throw error;
  }
};

/**
 * Fetches a specific exam session by ID
 * @param {number} sessionId - The ID of the session to fetch
 * @returns {Promise<Object>} - The session data
 */
export const getExamSessionById = async (sessionId) => {
  try {
    // Get the URL using resource link or fall back to the hardcoded endpoint
    const url = getResourceLink('exam', sessionId, 'self') || API_ENDPOINTS.EXAM_BY_ID(sessionId);
    
    const response = await apiRequest(url);
    
    // Extract and register all links from the response
    if (response && response.id) {
      extractAndRegisterLinks(response, 'exam');
    }
    
    return response;
  } catch (error) {
    console.error(`Error fetching exam session ${sessionId}:`, error);
    throw error;
  }
};

/**
 * Gets questions for a specific exam session
 * @param {number} sessionId - The ID of the session to get questions for
 * @param {Object} [paginationParams] - Optional pagination parameters
 * @param {number} [paginationParams.page] - Page number (starting from 0)
 * @param {number} [paginationParams.size] - Page size
 * @param {string} [paginationParams.sort] - Sorting criteria
 * @returns {Promise<Array>} - List of questions for the session
 */
export const getSessionQuestions = async (sessionId, paginationParams = null) => {
  try {
    // console.log('Fetching questions for session:', sessionId);
    
    // Try to get the questions link from registered links
    const questionsLink = getResourceLink('exam', sessionId, 'questions');
    
    // Use the link if available, otherwise use the hardcoded endpoint
    const url = questionsLink || API_ENDPOINTS.EXAM_QUESTIONS(sessionId);
      // If pagination parameters are provided, use fetchPaginatedData
    const response = paginationParams 
      ? await fetchPaginatedData(url, paginationParams) 
      : await apiRequest(url);
    
    // Process and register any links in the response
    if (Array.isArray(response)) {
      // console.log('Questions fetched successfully:', response.length);
      // Register links for each question
      response.forEach(question => {
        if (question && question.id) {
          extractAndRegisterLinks(question, 'question');
        }
      });
      return response;
    } else if (response && typeof response === 'object') {
      let questions = null;
      
      // Check for common wrapper formats
      for (const key of ['items', 'data', 'questions', 'examQuestions', 'results', 'content']) {
        if (Array.isArray(response[key])) {
          questions = response[key];
          break;
        }
      }
      
      // If we found questions in a wrapper
      if (questions) {
        // Register links for each question
        questions.forEach(question => {
          if (question && question.id) {
            extractAndRegisterLinks(question, 'question');
          }
        });
        
        // If this is a paginated response, store pagination metadata in the returned array
        if (response.totalElements !== undefined || 
            response.totalPages !== undefined ||
            response.size !== undefined ||
            response.number !== undefined) {
          questions.pagination = {
            totalElements: response.totalElements,
            totalPages: response.totalPages,
            size: response.size,
            page: response.number,
            hasNext: response.hasNext || (response.number < response.totalPages - 1)
          };
        }
        
        return questions;
      }
      
      // If the response itself is a collection with _links
      if (response._links) {
        extractAndRegisterLinks(response, 'questionCollection');
      }
    }
    
    console.warn('Unexpected response from questions endpoint:', response);
    return [];
  } catch (error) {
    console.error('Error fetching session questions:', error);
    return [];
  }
};

/**
 * Submits an answer for a question in a session
 * @param {number} sessionId - The ID of the session
 * @param {number} questionId - The ID of the question
 * @param {Object} answerData - The answer data (timeSpent, isFlagged, answers)
 * @param {string} [submitLink] - Optional HATEOAS link for submitting the answer
 * @returns {Promise<Object>} - The result of the submission
 */
export const submitAnswer = async (sessionId, questionId, answerData, submitLink) => {
  try {
    // console.log('Submitting answer:', { sessionId, questionId, answerData });
    
    // Priority 1: Use the HATEOAS link if provided
    if (submitLink) {
      // console.log(`Using provided submit link: ${submitLink}`);
      return await apiRequest(submitLink, {
        method: 'POST',
        body: JSON.stringify(answerData)
      });
    }
    
    // Priority 2: Check for a question-specific submit link
    const questionSubmitLink = getResourceLink('question', questionId, 'submit');
    if (questionSubmitLink) {
      // console.log(`Using question submit link: ${questionSubmitLink}`);
      return await apiRequest(questionSubmitLink, {
        method: 'POST',
        body: JSON.stringify(answerData)
      });
    }
    
    // Priority 3: Check for an answer link from the exam
    const examAnswerLink = getResourceLink('exam', sessionId, 'answer');
    if (examAnswerLink) {
      // console.log(`Using exam answer link: ${examAnswerLink}`);
      return await apiRequest(examAnswerLink, {
        method: 'POST',
        body: JSON.stringify({ ...answerData, questionId })
      });
    }
    
    // Priority 4: Use the correct RESTful endpoint format
    const url = API_ENDPOINTS.EXAM_ANSWER(sessionId, questionId);
    // console.log(`Using standard RESTful endpoint: ${url}`);
    return await apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(answerData)
    });
  } catch (error) {
    console.error('Error submitting answer:', error);
    throw error;
  }
};

/**
 * Submits feedback for a question
 * @param {Object} feedbackData - The feedback data
 * @param {number} feedbackData.questionId - The ID of the question
 * @param {string} feedbackData.feedback - The feedback text
 * @param {number} feedbackData.sessionId - The ID of the session
 * @returns {Promise<Object>} - The result of the submission
 */
export const submitFeedback = async (feedbackData) => {
  try {
    // console.log('Submitting feedback:', feedbackData);
    
    // Check if we have a feedback link for this question
    const feedbackLink = feedbackData.questionId ? 
      getResourceLink('question', feedbackData.questionId, 'feedback') : null;
      
    const url = feedbackLink || API_ENDPOINTS.FEEDBACK;
    
    return await apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(feedbackData)
    });
  } catch (error) {
    console.error('Error submitting feedback:', error);
    throw error;
  }
};

/**
 * Gets performance for a session
 * @param {number} sessionId - The ID of the session
 * @returns {Promise<Object>} - Session performance data
 */
export const getSessionPerformance = async (sessionId) => {
  try {
    // console.log('Fetching performance for session:', sessionId);
    
    // Check if we have a performance link for this session
    const performanceLink = getResourceLink('exam', sessionId, 'performance');
    const url = performanceLink || API_ENDPOINTS.EXAM_PERFORMANCE(sessionId);
    
    const response = await apiRequest(url);
    
    // Register any links in the performance data
    if (response && typeof response === 'object') {
      extractAndRegisterLinks(response, 'examPerformance');
    }
    
    return response;
  } catch (error) {
    console.error('Error fetching session performance:', error);
    throw error;
  }
};

/**
 * Gets responses for a specific exam session
 * @param {number} sessionId - The ID of the session to get responses for
 * @param {Object} [paginationParams] - Optional pagination parameters
 * @param {number} [paginationParams.page] - Page number (starting from 0)
 * @param {number} [paginationParams.size] - Page size
 * @param {string} [paginationParams.sort] - Sorting criteria
 * @returns {Promise<Array>} - List of responses for the session
 */
export const getSessionResponses = async (sessionId, paginationParams = null) => {
  try {
    // console.log('Fetching responses for session:', sessionId);
      // Try to get the responses link from registered links
    const responsesLink = getResourceLink('exam', sessionId, 'responses');
    
    // Use the link if available, otherwise use the hardcoded endpoint
    const url = responsesLink || API_ENDPOINTS.EXAM_RESPONSES(sessionId);
    
    // If pagination parameters are provided, use fetchPaginatedData
    const response = paginationParams 
      ? await fetchPaginatedData(url, paginationParams) 
      : await apiRequest(url);
    
    // Process responses and extract any contained links
    if (Array.isArray(response)) {
      // console.log('Responses fetched successfully:', response.length);
      response.forEach(resp => {
        if (resp && resp.id) {
          extractAndRegisterLinks(resp, 'response');
        }
      });
      return response;
    } else if (response && typeof response === 'object') {
      // Check for common wrapper formats
      for (const key of ['items', 'data', 'responses', 'examResponses', 'results', 'content']) {
        if (Array.isArray(response[key])) {
          const responses = response[key];
          
          responses.forEach(resp => {
            if (resp && resp.id) {
              extractAndRegisterLinks(resp, 'response');
            }
          });
          
          // If this is a paginated response, store pagination metadata in the returned array
          if (response.totalElements !== undefined || 
              response.totalPages !== undefined ||
              response.size !== undefined ||
              response.number !== undefined) {
            responses.pagination = {
              totalElements: response.totalElements,
              totalPages: response.totalPages,
              size: response.size,
              page: response.number,
              hasNext: response.hasNext || (response.number < response.totalPages - 1)
            };
          }
          
          return responses;
        }
      }
    }
    
    // Empty array is a valid response for new exams
    // console.log('No responses found or unexpected format, returning empty array');
    return [];
  } catch (error) {
    console.error('Error fetching session responses:', error);
    return [];
  }
};

/**
 * Gets AI assistance for a question
 * @param {Object} aiData - The AI request data
 * @param {number} aiData.questionId - The ID of the question
 * @param {string} aiData.query - The question to ask the AI
 * @param {number} aiData.sessionId - The ID of the session
 * @returns {Promise<Object>} - The AI response
 */
export const getAIAssistance = async (aiData) => {
  try {
    // console.log('Requesting AI assistance:', aiData);
    
    // Check if we have an AI assistance link for this question
    const aiAssistLink = aiData.questionId ? 
      getResourceLink('question', aiData.questionId, 'aiAssist') : null;
        const url = aiAssistLink || API_ENDPOINTS.AI_ASSIST;
    
    return await apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(aiData)
    });
  } catch (error) {
    console.error('Error requesting AI assistance:', error);
    throw error;
  }
};
